import { ComscoreContextualTaxonomy } from '~/src/dash/components/comscore/contextual/taxonomy';
import { IMenuList } from '~/src/dash/types/menuList';

export interface IComscoreContextualNode {
  children: IComscoreContextualNode[];
  value: string;
  data: IComscoreContextualData | undefined | null;
}

export interface IComscoreContextualData {
  ID: string;
  Name: string[];
  Description: string;
  Status: boolean;
}

export function generateComscoreContextualSegmentList(currentValues: string[]): IMenuList {
  const comscoreContextualData = parseComscoreContextualData(ComscoreContextualTaxonomy);
  const csList = {} as IMenuList;
  for (const data of comscoreContextualData) {
    // ignore the line if the ID is empty string
    if (data.ID === '') {
      continue;
    }

    csList[data.ID] = {
      title: data.Name[data.Name.length - 1],
      abbrevTitle: data.Name[data.Name.length - 1],
      selected: currentValues.includes(data.ID),
      ID: data.ID,
    };
  }

  return csList;
}

export function parseComscoreContextualData(customDataStore: string): IComscoreContextualData[] {
  const ComscoreContextualDataSet = new Set('');
  const dedupComscoreContextualData: IComscoreContextualData[] = [];
  const dataArray = customDataStore.split('\n');
  dataArray
    .filter((d: string) => d !== '')
    .forEach((d: string) => {
      let data: string[] = [] as string[];
      if (d.indexOf('"') === -1) {
        data = d.split(',');
      } else {
        // if the current string contains "", we use it as a wrapper, and do not split the ',' within it.
        while (d.length > 0) {
          if (d[0] !== '"') {
            // when the first char is not ", it means that the current item is not wrapped by "", we extract the item before the next comma and store it inside data array
            const commaIdx = d.indexOf(',');
            if (commaIdx === -1) {
              // if there is no comma, the current item is the last item
              data.push(d);
              break;
            } else {
              // extract the item before the next comma and store it inside data array
              data.push(d.substring(0, commaIdx));
            }

            if (commaIdx === d.length - 1) {
              // if comma is the last char in string, push an empty '' in and end the current iteration
              data.push('');
              break;
            } else {
              d = d.substring(commaIdx + 1);
            }
          } else {
            // if the first char is "
            // find out the position of the next "
            // and store the string between and store it inside data array
            d = d.substring(1);
            const quoteIdx = d.indexOf('"');
            data.push(d.substring(0, quoteIdx));
            d = d.substring(quoteIdx + 2);
          }
        }
      }

      const ComscoreContextualData: IComscoreContextualData = {
        ID: data[0] || '',
        Name: data[1].split(' > ') || ([] as string[]),
        Description: data[2] || '',
        Status: data[3] === 'Active' ? true : false,
      };

      if (ComscoreContextualData.Description === '' && ComscoreContextualData.Name.length > 0) {
        ComscoreContextualData.Description =
          ComscoreContextualData.Name[ComscoreContextualData.Name.length - 1];
      }

      // Brand Protection is duplicative with the IAS GARM offerings, remove it from UI
      if (
        ComscoreContextualData.Name.length === 0 ||
        ComscoreContextualData.Name[0] === 'Brand Protection'
      ) {
        return;
      }

      if (
        ComscoreContextualData.ID !== '' &&
        !ComscoreContextualDataSet.has(ComscoreContextualData.ID)
      ) {
        ComscoreContextualDataSet.add(ComscoreContextualData.ID);
        dedupComscoreContextualData.push(ComscoreContextualData);
      }
    });

  return dedupComscoreContextualData;
}

export function parseComscoreContextualTree(
  rawData: IComscoreContextualData[],
): IComscoreContextualNode[] {
  const ComscoreContextualTree = [] as IComscoreContextualNode[];
  for (const d of rawData) {
    let currentLevel = ComscoreContextualTree;
    for (let i = 0; i < d.Name.length; i += 1) {
      if (currentLevel.length === 0) {
        const newNode: IComscoreContextualNode = Object.assign(
          {
            children: [] as IComscoreContextualNode[],
            value: d.Name[i],
          },
          i === d.Name.length - 1 ? { data: d } : { data: undefined },
        );
        currentLevel.push(newNode);
        if (i !== d.Name.length - 1) {
          currentLevel = newNode.children;
        }
      } else {
        for (let j = 0; j < currentLevel.length; j += 1) {
          if (currentLevel[j].value === d.Name[i]) {
            currentLevel = currentLevel[j].children;
            break;
          } else {
            if (j === currentLevel.length - 1) {
              const newNode: IComscoreContextualNode = Object.assign(
                {
                  children: [] as IComscoreContextualNode[],
                  value: d.Name[i],
                },
                i === d.Name.length - 1 ? { data: d } : { data: undefined },
              );
              currentLevel.push(newNode);
              if (i !== d.Name.length - 1) {
                currentLevel = newNode.children;
              }
            }
          }
        }
      }
    }
  }

  return ComscoreContextualTree;
}
